import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { LocalStorageKeys } from '@constants/enums';
import { TIMEOUTS } from '@constants/auth.constants';
import { ROUTES } from '@configs/routes';
import authSvc from '../../../services/auth/authSvc';
import {
  cognitoForgotPasswordSchema,
  CognitoForgotPasswordData,
} from '../../../schemas/auth.schemas';
import useAuthErrorHandler from '../../shared/hooks/useAuthErrorHandler';
import { UseCognitoForgotPasswordReturn } from '../types';

const useCognitoForgotPassword = (): UseCognitoForgotPasswordReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useAuthErrorHandler('forgot-password');

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<CognitoForgotPasswordData>({
    resolver: zodResolver(cognitoForgotPasswordSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  const { register, handleSubmit, formState, setValue } = form;

  const getFieldError = useCallback(
    (fieldName: keyof CognitoForgotPasswordData): string => {
      const fieldError = formState.errors[fieldName];
      if (!fieldError?.message) return '';
      return t(fieldError.message);
    },
    [formState.errors, t]
  );

  const handleBackToLogin = useCallback(() => {
    navigate(ROUTES.AUTH.LOGIN);
  }, [navigate]);

  const onSubmit = useCallback(
    async (data: CognitoForgotPasswordData) => {
      try {
        setIsLoading(true);
        setError('');
        setSuccess(false);

        const response = await authSvc.cognitoForgotPassword(
          data.email.toLowerCase()
        );

        if (response) {
          setSuccess(true);

          localStorage.setItem(
            LocalStorageKeys.RESET_EMAIL_PASSWORD,
            data.email.toLowerCase()
          );

          setTimeout(() => {
            navigate(ROUTES.AUTH.PASSWORD);
          }, TIMEOUTS.PASSWORD_RESET_SUCCESS);
        }
      } catch (err: any) {
        console.error('Cognito forgot password error:', err);
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, getErrorMessage]
  );

  useEffect(() => {
    const savedEmail = localStorage.getItem(
      LocalStorageKeys.RESET_EMAIL_PASSWORD
    );
    if (savedEmail) {
      setValue('email', savedEmail, { shouldValidate: true });
    }
  }, [setValue]);

  return {
    register,
    handleSubmit,
    formState,
    success,
    error,
    isLoading,
    handleBackToLogin,
    onSubmit,
    getFieldError,
  };
};

export default useCognitoForgotPassword;
